import React, { useEffect, useState } from "react";
import { Table, Button, Space, Tag, Switch, Modal, message, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEdit, faTrash, faUpRightFromSquare, faCopy } from "@fortawesome/free-solid-svg-icons";
import landingPageApi from "../../../api/landingPageApi";
import { audienceColumn } from "../../../components/audience-fields";
import { landingPageUrl } from "./landing-page-url";
import LandingPageDesignRules from "./LandingPageDesignRules";

/**
 * Danh sách Trang HTML (landing page dạng ladipage).
 * Mỗi trang có URL /trang/:slug — copy vào Tiện ích App (openMode "App view")
 * để hiện ngay trong app.
 */
function LandingPageList() {
    const navigate = useNavigate();
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchPages = async () => {
        try {
            setLoading(true);
            const { data } = await landingPageApi.getAll();
            setPages(data.data || []);
        } catch {
            message.error("Không thể tải danh sách trang");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPages();
    }, []);

    const handleToggle = async (id) => {
        try {
            await landingPageApi.toggle(id);
            message.success("Đã cập nhật trạng thái");
            fetchPages();
        } catch {
            message.error("Không thể cập nhật trạng thái");
        }
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: "Xác nhận xoá",
            content: `Xoá trang "${record.title}"? Link /trang/${record.slug} sẽ không truy cập được nữa.`,
            okText: "Xoá",
            cancelText: "Huỷ",
            okType: "danger",
            onOk: async () => {
                try {
                    await landingPageApi.delete(record.id);
                    message.success("Đã xoá trang");
                    fetchPages();
                } catch {
                    message.error("Không thể xoá trang");
                }
            },
        });
    };

    const copyUrl = async (slug) => {
        try {
            await navigator.clipboard.writeText(landingPageUrl(slug));
            message.success("Đã copy link trang");
        } catch {
            message.error("Trình duyệt chặn copy — mở link rồi copy thủ công");
        }
    };

    const columns = [
        {
            title: "Tiêu đề",
            dataIndex: "title",
            render: (title, record) => (
                <div>
                    <div className="font-semibold">{title}</div>
                    {record.description && (
                        <div className="text-xs text-content-3">{record.description}</div>
                    )}
                </div>
            ),
        },
        {
            title: "Đường dẫn",
            dataIndex: "slug",
            render: (slug) => <code className="text-xs">/trang/{slug}</code>,
        },
        audienceColumn(),
        {
            title: "Lượt xem",
            dataIndex: "views",
            width: 100,
            render: (views) => <Tag color="blue">{views ?? 0}</Tag>,
        },
        {
            title: "Hiển thị",
            dataIndex: "isActive",
            width: 100,
            render: (isActive, record) => (
                <Switch checked={!!isActive} onChange={() => handleToggle(record.id)} />
            ),
        },
        {
            title: "Thao tác",
            width: 190,
            render: (_, record) => (
                <Space>
                    <Tooltip title="Copy link (dán vào Tiện ích App)">
                        <Button size="small" onClick={() => copyUrl(record.slug)}>
                            <FontAwesomeIcon icon={faCopy} />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Mở trang">
                        <Button
                            size="small"
                            href={landingPageUrl(record.slug)}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <FontAwesomeIcon icon={faUpRightFromSquare} />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Sửa">
                        <Button size="small" onClick={() => navigate(`/page/${record.id}/edit`)}>
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Xoá">
                        <Button size="small" danger onClick={() => handleDelete(record)}>
                            <FontAwesomeIcon icon={faTrash} />
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div className="p-4">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="text-xl font-bold m-0">Trang HTML</h2>
                    <p className="text-xs text-content-3 m-0">
                        Dán nguyên file HTML (ladipage) → có link /trang/:slug để nhúng vào Tiện ích App.
                    </p>
                </div>
                <Space>
                    <LandingPageDesignRules />
                    <Button type="primary" onClick={() => navigate("/page/create")}>
                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                        Tạo trang
                    </Button>
                </Space>
            </div>

            <Table
                rowKey="id"
                columns={columns}
                dataSource={pages}
                loading={loading}
                pagination={{ pageSize: 20 }}
                scroll={{ x: 800 }}
            />
        </div>
    );
}

export default LandingPageList;
